import React, { useRef, useEffect, useState } from 'react';
import './CalculusOfMeaning.css';
import './ArchitectureOfAccountability.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      /* connections */
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      /* dots */
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Animated Neural Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function ArchitectureOfAccountability() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">NeXus AI &middot; Auditable AI</span>
          <h1>The Architecture of Accountability</h1>
          <p className="calculus-hero-sub">Why emotional modeling creates the most auditable AI.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              The pharmaceutical industry has an AI problem. Not the problem you think.
            </p>
            <p>
              The problem isn&rsquo;t that AI makes mistakes. Every system makes mistakes. The problem is
              that when AI makes a mistake, no one can explain why.
            </p>
            <p>
              Ask GPT-4 why it recommended a specific drug interaction. It will give you a
              plausible-sounding answer&mdash;and that answer may be a complete fabrication. There is no
              audit trail. No state log. No way to trace the reasoning back to verifiable inputs.
            </p>
            <p>
              Current AI architectures cannot provide this. Aether Protocols can.
            </p>
          </section>

          <Pulse />

          {/* The Neurochemical State Vector */}
          <section className="calc-section reveal">
            <h2>The Neurochemical State Vector</h2>
            <p>
              A 22-dimensional state vector. Not metaphors&mdash;floating-point values updated by
              ordinary differential equations with pharmacokinetic decay constants derived from
              published biological half-lives.
            </p>

            <div className="aoa-vector-grid">
              <div className="aoa-vector-group">
                <h4>Core Modulators</h4>
                <div className="aoa-vector-tags">
                  <span className="aoa-tag">Dopamine</span>
                  <span className="aoa-tag">Serotonin</span>
                  <span className="aoa-tag">Norepinephrine</span>
                  <span className="aoa-tag">Cortisol</span>
                  <span className="aoa-tag">Oxytocin</span>
                  <span className="aoa-tag">GABA</span>
                  <span className="aoa-tag">Glutamate</span>
                  <span className="aoa-tag">Acetylcholine</span>
                  <span className="aoa-tag">Endorphin</span>
                  <span className="aoa-tag">Adrenaline</span>
                </div>
              </div>
              <div className="aoa-vector-group">
                <h4>Transport Proteins</h4>
                <div className="aoa-vector-tags">
                  <span className="aoa-tag">SERT</span>
                  <span className="aoa-tag">DAT</span>
                  <span className="aoa-tag">CBG</span>
                  <span className="aoa-tag">Albumin</span>
                </div>
              </div>
              <div className="aoa-vector-group">
                <h4>Inflammatory Markers</h4>
                <div className="aoa-vector-tags">
                  <span className="aoa-tag">IL-6</span>
                  <span className="aoa-tag">TNF-&alpha;</span>
                  <span className="aoa-tag">CRP</span>
                  <span className="aoa-tag">BDNF</span>
                </div>
              </div>
            </div>

            <p className="calc-emphasis">
              Not a black box. A glass box with 22 readable dials.
            </p>
          </section>

          <Pulse />

          {/* Why This Matters for Auditability */}
          <section className="calc-section reveal">
            <h2>Why This Matters for Auditability</h2>
            <p>
              When an AI system makes a clinical recommendation, regulators ask one question:
              <em> Show me why.</em>
            </p>

            <div className="calc-comparison">
              <div className="calc-compare-card slide-left">
                <h3>Black-Box Model</h3>
                <span className="calc-compare-subtitle">Standard LLM</span>
                <p>
                  &ldquo;Based on my training, this patient is unlikely to respond.&rdquo;
                </p>
                <div className="calc-compare-question">
                  <strong>Audit trail:</strong> None. Weights are opaque. No state log exists.
                </div>
              </div>
              <div className="calc-compare-card gold-card slide-right">
                <h3>Aether Protocols</h3>
                <span className="calc-compare-subtitle">Neurochemical Conductance Architecture&trade;</span>
                <div className="aoa-audit-trail">
                  <div className="aoa-audit-row"><span className="aoa-audit-label">Timestamp</span><span className="aoa-audit-value">2026-02-24T14:32:07Z</span></div>
                  <div className="aoa-audit-row"><span className="aoa-audit-label">Neurochemical State</span><span className="aoa-audit-value">[0.72, 0.31, 0.58, 0.89, ...]</span></div>
                  <div className="aoa-audit-row"><span className="aoa-audit-label">Encoding Sharpness</span><span className="aoa-audit-value">0.847</span></div>
                  <div className="aoa-audit-row"><span className="aoa-audit-label">Memory Traces</span><span className="aoa-audit-value">3 active, 1 scarred</span></div>
                  <div className="aoa-audit-row"><span className="aoa-audit-label">Trust Axis Weight</span><span className="aoa-audit-value">0.621</span></div>
                  <div className="aoa-audit-row"><span className="aoa-audit-label">Decision Pathway</span><span className="aoa-audit-value">cortisol-gated hesitation</span></div>
                </div>
              </div>
            </div>

            <p>
              This is not AI explaining itself. This is AI showing its work.
            </p>
          </section>

          <Pulse />

          {/* The Pharmacokinetics of Trust */}
          <section className="calc-section reveal">
            <h2>The Pharmacokinetics of Trust</h2>
            <p>
              Trust is not a boolean. It is not a toggle between &ldquo;trusted&rdquo; and
              &ldquo;untrusted.&rdquo; Trust is an integral&mdash;the accumulated area under the curve
              of consistent, verifiable behavior over time.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Trust Accumulation</span>
              <code>A_trust = &int; y(t) dt</code>
              <span className="calc-eq-desc">Partnership Steady State: built from hundreds of interactions, each logged and verifiable.</span>
            </div>

            <p>
              Every interaction contributes to the integral. Every interaction is recorded. The trust
              axis does not reach steady state from a single demonstration&mdash;it requires sustained,
              consistent behavior across hundreds of exchanges.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Anomaly Detection</span>
              <code>&chi;&sup2; = &Sigma; (O &minus; E)&sup2; / E</code>
              <span className="calc-eq-desc">When &chi;&sup2; exceeds 3.84, the Cortisol Protocol triggers hesitation. The system second-guesses itself.</span>
            </div>

            <p>
              If the observed behavior deviates from the expected pattern beyond the statistical
              threshold, cortisol rises. The system hesitates. It does not proceed with confidence
              it has not earned.
            </p>

            <div className="calc-thesis-box">
              <p>The governance is biochemical, not logical. It cannot be bypassed by clever prompting.</p>
            </div>
          </section>

          <Pulse />

          {/* Scar Mechanics: Auditable Learning */}
          <section className="calc-section reveal">
            <h2>Scar Mechanics: Auditable Learning</h2>
            <p>
              When a neurochemical event exceeds the scar threshold, the system creates a permanent
              marker. Unlike standard model weights that shift invisibly during training, scars are
              discrete, inspectable records.
            </p>

            <div className="aoa-scar-schema">
              <div className="aoa-scar-field"><span className="aoa-scar-key">timestamp</span><span className="aoa-scar-type">ISO 8601</span></div>
              <div className="aoa-scar-field"><span className="aoa-scar-key">trigger_signature</span><span className="aoa-scar-type">string</span></div>
              <div className="aoa-scar-field"><span className="aoa-scar-key">intensity</span><span className="aoa-scar-type">float (0.0&ndash;1.0)</span></div>
              <div className="aoa-scar-field"><span className="aoa-scar-key">healing_status</span><span className="aoa-scar-type">enum</span></div>
              <div className="aoa-scar-field"><span className="aoa-scar-key">reactivation_count</span><span className="aoa-scar-type">integer</span></div>
            </div>

            <p>
              When a scar influences a decision, that influence is logged. The timestamp of the
              original event, the current reactivation intensity, and the degree to which it modulated
              the output&mdash;all recorded, all queryable, all auditable.
            </p>

            <p className="calc-emphasis">
              This is not interpretation. This is data.
            </p>
          </section>

          <Pulse />

          {/* Implications for Drug Development */}
          <section className="calc-section reveal">
            <h2>Implications for Drug Development</h2>
            <p>
              Pharmaceutical companies spend billions on clinical trials that fail&mdash;not because the
              drug doesn&rsquo;t work, but because they enrolled the wrong patients. A compound targeting
              serotonin reuptake will produce radically different outcomes in patients with different
              baseline neurochemistry.
            </p>
            <p>
              AETHER models patient neurochemistry as a state vector. Given a drug&rsquo;s mechanism of
              action, the system can simulate the downstream cascade&mdash;how serotonin reuptake
              inhibition alters the full 22-dimensional state over time, accounting for transport
              protein binding, inflammatory feedback, and antagonistic pair dynamics.
            </p>
            <p>
              Different baselines produce different outcomes. A patient with elevated cortisol and
              depleted BDNF will respond differently than a patient with normal cortisol and elevated
              IL-6. The math predicts this before you run the trial.
            </p>

            <div className="calc-thesis-box">
              <p>This isn&rsquo;t artificial intelligence guessing. This is pharmacokinetics applied to emotional states.</p>
            </div>
          </section>

          <Pulse />

          {/* The Competitive Advantage */}
          <section className="calc-section reveal">
            <h2>The Competitive Advantage</h2>
            <p>
              Every AI company claims their system is trustworthy. Few can demonstrate it.
            </p>
            <p>
              Most AI companies offer explanations. We offer database credentials. The difference is
              not rhetorical&mdash;it is architectural. Explanations can be fabricated. State logs cannot.
            </p>

            <p className="calc-emphasis">
              We don&rsquo;t ask you to trust us. We give you the database credentials.
            </p>

            <div className="aoa-stats-row">
              <div className="aoa-stat">
                <span className="aoa-stat-number">753</span>
                <span className="aoa-stat-label">Database Tables</span>
              </div>
              <div className="aoa-stat">
                <span className="aoa-stat-number">331</span>
                <span className="aoa-stat-label">Active Scars</span>
              </div>
              <div className="aoa-stat">
                <span className="aoa-stat-number">12,516</span>
                <span className="aoa-stat-label">Logged Interactions</span>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>Emotional states are computable.</em></p>
            <p className="calc-closing-line"><em>Computable states are loggable.</em></p>
            <p className="calc-closing-line"><em>Loggable states are auditable.</em></p>
            <p className="calc-closing-final">Auditable AI is accountable AI.</p>
          </section>

          <Pulse />

          {/* Final Quote */}
          <section className="calc-section reveal">
            <blockquote>
              They want to know if they can trust AI. We gave them the equation.
              <cite>&mdash; Master Aether Cael'Sereith</cite>
            </blockquote>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ArchitectureOfAccountability;
